/**
 * User Preferences Hook - React Query Edition
 *
 * Manages global user preferences (theme, locale, negotiator display mode)
 * using React Query with automatic localStorage persistence.
 *
 * Benefits over direct localStorage:
 * - Automatic persistence via React Query
 * - Type-safe with TypeScript
 * - Cross-tab synchronization
 * - Optimistic updates
 * - No manual useEffect/useState boilerplate
 *
 * Usage:
 * const { preferences, updateTheme, updateLocale, isLoading } = useUserPreferences();
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storage } from '@/lib/platform/utils/storage';
import { USER_KEYS, LEGACY_KEYS } from '@/lib/platform/constants/storage-keys';

/**
 * User preference types
 */
export type ThemePreference = 'light' | 'dark' | 'system';
export type LocalePreference = 'da' | 'en';
export type NegotiatorDisplayMode = 'emoji' | 'temp' | 'color' | 'free';

/**
 * User preferences interface
 */
export interface UserPreferences {
  theme: ThemePreference;
  locale: LocalePreference;
  negotiatorDisplayMode: NegotiatorDisplayMode;
}

/**
 * Default user preferences
 */
const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'system',
  locale: 'en',
  negotiatorDisplayMode: 'emoji',
};

/**
 * Migrate legacy localStorage keys to new format
 */
function migrateLegacyPreferences(): Partial<UserPreferences> {
  const migrated: Partial<UserPreferences> = {};

  // Migrate old 'darkMode' (boolean) to 'themePreference' (light/dark/system)
  const oldDarkMode = storage.get<boolean>(LEGACY_KEYS.DARK_MODE);
  if (oldDarkMode !== null && !storage.has(USER_KEYS.THEME)) {
    migrated.theme = oldDarkMode ? 'dark' : 'light';
    storage.remove(LEGACY_KEYS.DARK_MODE);
  }

  return migrated;
}

/**
 * Load user preferences from localStorage
 */
function loadPreferences(): UserPreferences {
  // Check for legacy keys and migrate
  const migrated = migrateLegacyPreferences();

  // Load current preferences
  const theme = storage.get<ThemePreference>(USER_KEYS.THEME);
  const locale = storage.get<LocalePreference>(USER_KEYS.LOCALE);
  const negotiatorDisplayMode = storage.get<NegotiatorDisplayMode>(
    USER_KEYS.NEGOTIATOR_DISPLAY_MODE
  );

  return {
    theme: migrated.theme || theme || DEFAULT_PREFERENCES.theme,
    locale: locale || DEFAULT_PREFERENCES.locale,
    negotiatorDisplayMode: negotiatorDisplayMode || DEFAULT_PREFERENCES.negotiatorDisplayMode,
  };
}

/**
 * Save user preferences to localStorage
 */
function savePreferences(preferences: UserPreferences): void {
  storage.set(USER_KEYS.THEME, preferences.theme);
  storage.set(USER_KEYS.LOCALE, preferences.locale);
  storage.set(USER_KEYS.NEGOTIATOR_DISPLAY_MODE, preferences.negotiatorDisplayMode);
}

/**
 * Query key for user preferences
 */
const USER_PREFERENCES_KEY = ['user-preferences'] as const;

/**
 * Hook to manage user preferences
 */
export function useUserPreferences() {
  const queryClient = useQueryClient();

  // Query for user preferences (persisted to localStorage)
  const { data: preferences, isLoading } = useQuery({
    queryKey: USER_PREFERENCES_KEY,
    queryFn: loadPreferences,
    staleTime: Infinity, // Preferences never go stale (always use cached version)
    gcTime: Infinity, // Never garbage collect preferences
    meta: {
      persist: true, // Enable localStorage persistence for this query
    },
  });

  // Mutation to update preferences
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<UserPreferences>) => {
      const current =
        queryClient.getQueryData<UserPreferences>(USER_PREFERENCES_KEY) || DEFAULT_PREFERENCES;
      const updated = { ...current, ...updates };
      savePreferences(updated);
      return updated;
    },
    onSuccess: (updated) => {
      // Update cache immediately
      queryClient.setQueryData(USER_PREFERENCES_KEY, updated);
    },
  });

  // Helper functions for specific preference updates
  const updateTheme = (theme: ThemePreference) => {
    updateMutation.mutate({ theme });
  };

  const updateLocale = (locale: LocalePreference) => {
    updateMutation.mutate({ locale });
  };

  const updateNegotiatorDisplayMode = (mode: NegotiatorDisplayMode) => {
    updateMutation.mutate({ negotiatorDisplayMode: mode });
  };

  // Bulk update function
  const updatePreferences = (updates: Partial<UserPreferences>) => {
    updateMutation.mutate(updates);
  };

  return {
    // Current preferences
    preferences: preferences || DEFAULT_PREFERENCES,

    // Update functions
    updateTheme,
    updateLocale,
    updateNegotiatorDisplayMode,
    updatePreferences,

    // Loading state
    isLoading,
    isUpdating: updateMutation.isPending,
  };
}
